// resources/js/native-downloads.js
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capawesome-team/capacitor-file-opener';

const EXTENSIONS = ['.pdf', '.csv', '.xlsx', '.xls', '.zip', '.doc', '.docx', '.xml', '.txt'];

const isNative = () => Capacitor.isNativePlatform();

function shouldIntercept(link) {
  if (!link) return false;
  if (!isNative()) return false;

  const href = (link.getAttribute('href') || '').trim();
  if (!href || href.startsWith('mailto:') || href.startsWith('tel:')) return false;

  try {
    const u = new URL(href, window.location.origin);
    if (u.origin !== window.location.origin) return false;
  } catch {
    return false;
  }

  if (link.classList.contains('js-native-download')) return true;
  if (link.hasAttribute('download')) return true;
  if (link.dataset.nativeDownload === 'true') return true;

  const lowerHref = href.toLowerCase();
  if (lowerHref.includes('/pdf/') || lowerHref.includes('/download=')) return true;
  if (EXTENSIONS.some(ext => lowerHref.endsWith(ext))) return true;

  return false;
}

function guessMime(filename) {
  const f = filename.toLowerCase();
  if (f.endsWith('.pdf')) return 'application/pdf';
  if (f.endsWith('.csv')) return 'text/csv';
  if (f.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (f.endsWith('.xls')) return 'application/vnd.ms-excel';
  if (f.endsWith('.zip')) return 'application/zip';
  if (f.endsWith('.doc')) return 'application/msword';
  if (f.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (f.endsWith('.xml')) return 'application/xml';
  if (f.endsWith('.txt')) return 'text/plain';
  return 'application/octet-stream';
}

function parseContentDispositionFilename(value) {
  if (!value) return null;
  const star = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(value);
  if (star && star[2]) {
    try { return decodeURIComponent(star[2]); } catch { return star[2]; }
  }
  const normal = /filename\s*=\s*"?([^";]+)"?/i.exec(value);
  return normal ? normal[1] : null;
}

function filenameFromUrl(url, contentType) {
  try {
    const u = new URL(url, window.location.origin);
    const last = u.pathname.split('/').filter(Boolean).pop() || 'file';
    if (last.includes('.')) return last;
    if ((contentType || '').includes('pdf')) return `${last}.pdf`;
    return last;
  } catch {
    return 'file';
  }
}

async function arrayBufferToBase64(ab) {
  const blob = new Blob([ab]);
  const reader = new FileReader();
  return await new Promise((resolve, reject) => {
    reader.onload = () => {
      const res = reader.result || '';
      const base64 = res.toString().split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// NEW: descarga y guarda (reutilizable para visor/abrir)
async function downloadFile(url, explicitName) {
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Accept': '*/*' }
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const contentType = res.headers.get('content-type') || '';
  const dispo = res.headers.get('content-disposition') || '';
  const fromHeader = parseContentDispositionFilename(dispo);
  const filename = explicitName || fromHeader || filenameFromUrl(url, contentType);
  const mime = contentType || guessMime(filename);

  const ab = await res.arrayBuffer();
  const base64 = await arrayBufferToBase64(ab);

  // Guardar en caché
  await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
  });
  const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });

  // Crear blobUrl para visor inline
  let blobUrl = null;
  try {
    const blob = new Blob([ab], { type: mime });
    blobUrl = URL.createObjectURL(blob);
  } catch {}

  return { filename, mime, uri, blobUrl };
}

// EXISTENTE: abrir nativo directamente
async function downloadAndOpen(url, explicitName) {
  const { mime, uri } = await downloadFile(url, explicitName);
  await FileOpener.open({ filePath: uri, contentType: mime });
}

// NEW: overlay visor con botón Atrás (para PDFs)
const OVERLAY_ID = 'native-pdf-overlay';
function injectOverlayStylesOnce() {
  if (document.getElementById(`${OVERLAY_ID}-styles`)) return;
  const style = document.createElement('style');
  style.id = `${OVERLAY_ID}-styles`;
  style.textContent = `
    #${OVERLAY_ID}{
      position:fixed; inset:0; z-index:9999; display:flex; flex-direction:column;
      background:#111;
    }
    #${OVERLAY_ID} .bar{
      position:sticky; top:0; display:flex; gap:.5rem; align-items:center;
      padding: calc(env(safe-area-inset-top,0) + 8px) 12px 8px 12px;
      background:#0A84FF; color:#fff; z-index:1;
      min-height:48px;
    }
    #${OVERLAY_ID} .bar .spacer{ flex:1 1 auto; }
    #${OVERLAY_ID} .bar .btn{
      appearance:none; border:0; background:transparent; color:inherit; font-size:16px;
    }
    #${OVERLAY_ID} iframe{
      border:0; width:100%; height:100%;
      background:#333;
    }
  `;
  document.head.appendChild(style);
}

function closePdfOverlay() {
  const el = document.getElementById(OVERLAY_ID);
  if (el) {
    const iframe = el.querySelector('iframe');
    if (iframe && iframe.src.startsWith('blob:')) {
      try { URL.revokeObjectURL(iframe.src); } catch {}
    }
    el.remove();
  }
}

function showPdfOverlay({ blobUrl, downloadUrl, filename, onOpenExternal }) {
  injectOverlayStylesOnce();
  closePdfOverlay();
  const wrap = document.createElement('div');
  wrap.id = OVERLAY_ID;

  const bar = document.createElement('div');
  bar.className = 'bar';
  bar.style.background = '#a6342f';

  const backBtn = document.createElement('button');
  backBtn.className = 'btn';
  backBtn.textContent = '← Back';
  backBtn.addEventListener('click', () => closePdfOverlay());

  // const openBtn = document.createElement('button');
  // openBtn.className = 'btn';
  // openBtn.textContent = 'Abrir con…';
  // openBtn.addEventListener('click', async () => {
  //   try { await onOpenExternal?.(); } finally { /* mantenemos el overlay para que puedan volver */ }
  // });

  // const downloadA = document.createElement('a');
  // downloadA.className = 'btn';
  // downloadA.href = downloadUrl;
  // downloadA.setAttribute('download', filename);
  // downloadA.textContent = 'Descargar';

  // const spacer = document.createElement('div');
  // spacer.className = 'spacer';

  bar.appendChild(backBtn);
  // bar.appendChild(spacer);
  // bar.appendChild(openBtn);
  // bar.appendChild(downloadA);

  const iframe = document.createElement('iframe');
  iframe.src = blobUrl || downloadUrl; // en Android puede que no renderice blob; dejamos URL como fallback

  wrap.appendChild(bar);
  wrap.appendChild(iframe);
  document.body.appendChild(wrap);
}

// Delegación global de clicks
document.addEventListener('click', async (e) => {
  const link = e.target.closest('a[href]');
  if (!shouldIntercept(link)) return;

  e.preventDefault();
  e.stopPropagation();

  const href = link.href;
  const explicitName = link.getAttribute('data-filename') || null;
  const forceNative = link.dataset.forceNative === '1'; // por si algún botón quiere forzar nativo

  // ¿Es PDF?
  const isPdf = /\.pdf($|\?)/i.test(href) || href.toLowerCase().includes('/pdf/');

  try {
    if (isPdf && !forceNative) {
      // Descarga y muestra overlay con botón Atrás
      const file = await downloadFile(href, explicitName);
      showPdfOverlay({
        blobUrl: file.blobUrl,
        downloadUrl: href,
        filename: file.filename,
        onOpenExternal: () => FileOpener.open({ filePath: file.uri, contentType: file.mime }),
      });
    } else {
      // Resto de tipos (o forzar nativo): descarga + abre con visor del sistema
      await downloadAndOpen(href, explicitName);
    }
  } catch (err) {
    console.error('[native-downloads] Error abriendo archivo:', err);
    // Fallback: si algo falla, navega normal
    window.location.href = href;
  }
}, true);
